import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle, Shield, Clock, ArrowRight } from 'lucide-react';
import './MerchantAuth.css';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/merchant/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('If an account with that email exists, a password reset link has been sent.');
      } else {
        throw new Error(data.error || 'Failed to send reset link');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="merchant-auth-container">
      {/* Left Side - Premium Panel */}
      <div className="merchant-auth-left">
        {/* Floating orbs */}
        <div className="merchant-auth-left-orb merchant-auth-left-orb-1" />
        <div className="merchant-auth-left-orb merchant-auth-left-orb-2" />
        <div className="merchant-auth-left-orb merchant-auth-left-orb-3" />

        <div className="merchant-auth-left-content">
          {/* Logo badge */}
          <div className="merchant-auth-left-logo">
            <img src="/logo192.png" alt="Revstar" />
            <span>Revstar</span>
          </div>

          {/* Glassmorphism card */}
          <div className="merchant-auth-slide-wrapper">
            <div className="merchant-auth-slide-card">
              <div className="merchant-auth-slide-icon">
                <Shield size={28} />
              </div>
              <h3 className="merchant-auth-slide-title">
                Account Recovery
              </h3>
              <p className="merchant-auth-slide-desc">
                We'll help you get back into your account safely and securely.
              </p>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                marginTop: '1.5rem',
                width: '100%',
                maxWidth: '280px'
              }}>
                {[
                  { icon: Shield, text: 'Secure reset process' },
                  { icon: Mail, text: 'Email verification required' },
                  { icon: Clock, text: 'Link expires in 1 hour' }
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    fontSize: '0.875rem',
                    color: 'rgba(167, 243, 208, 0.85)'
                  }}>
                    <item.icon size={16} style={{ flexShrink: 0, opacity: 0.7 }} />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Forgot Password Form */}
      <div className="merchant-auth-right">
        <div className="merchant-auth-form-container">
          {/* Mobile logo */}
          <div className="merchant-auth-logo">
            <img src="/logo192.png" alt="Revstar" className="merchant-auth-logo-img" />
            <span className="merchant-auth-logo-text">Revstar</span>
          </div>

          {/* Header */}
          <div className="merchant-auth-header">
            <h2 className="merchant-auth-title">Reset Your Password</h2>
            <p className="merchant-auth-subtitle">
              Enter your email address and we'll send you a reset link
            </p>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="merchant-auth-alert error">
              <AlertCircle className="merchant-auth-alert-icon" />
              <span className="merchant-auth-alert-text">{error}</span>
            </div>
          )}

          {success && (
            <div className="merchant-auth-alert success">
              <CheckCircle className="merchant-auth-alert-icon" />
              <span className="merchant-auth-alert-text">{success}</span>
            </div>
          )}

          {/* Forgot Password Form */}
          <form onSubmit={handleSubmit} className="merchant-auth-form">
            <div className="merchant-auth-field">
              <label className="merchant-auth-label">
                Email Address
              </label>
              <div className="merchant-auth-input-container">
                <Mail className="merchant-auth-input-icon" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="merchant-auth-input"
                  placeholder="Enter your email address"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email}
              className="merchant-auth-button"
            >
              {loading ? (
                <>
                  <div className="merchant-auth-spinner" />
                  <span>Sending Reset Link...</span>
                </>
              ) : (
                <>
                  <span>Send Reset Link</span>
                  <ArrowRight style={{ width: '1.25rem', height: '1.25rem' }} />
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="merchant-auth-footer">
            <p className="merchant-auth-footer-text">
              Remember your password?{' '}
              <button
                onClick={() => navigate('/login')}
                className="merchant-auth-footer-link"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
